import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ChevronDown, Check, Mic, Sparkles, Map, FileText,
  Building2, Users, Inbox, Heart, Plug, BarChart3, MessageSquare,
  Mail, Phone, MapPin, ChevronRight, Star, Zap, Shield, Globe,
  Workflow, BadgeCheck, Volume2, VolumeX,
} from 'lucide-react';
import DeoLogo from '../components/DeoLogo';

/* ============ DESIGN TOKENS ============ */
const pageBg = {
  background: `
    radial-gradient(ellipse 80% 60% at 20% 0%, rgba(244, 213, 184, 0.7), transparent 60%),
    radial-gradient(ellipse 60% 50% at 90% 100%, rgba(232, 184, 148, 0.55), transparent 65%),
    radial-gradient(ellipse 100% 100% at 50% 50%, #F4ECDF 0%, #E8DECD 50%, #D8CAB1 100%)
  `,
};

/* Common eyebrow style — standartlaştırıldı */
const Eyebrow = ({ children, light = false, testid }) => (
  <div
    className={`font-body text-[10px] tracking-[0.32em] font-semibold uppercase ${light ? 'text-[#E08A4F]' : 'text-[#8E4A23]'}`}
    data-testid={testid}
  >
    {children}
  </div>
);

const SectionHead = ({ eyebrow, title, accent, sub, light = false, center = false }) => (
  <div className={`max-w-[760px] ${center ? 'mx-auto text-center' : ''} mb-12`}>
    <Eyebrow light={light}>{eyebrow}</Eyebrow>
    <h2 className={`font-display text-[40px] sm:text-[48px] leading-[1.1] font-light tracking-tight mt-3 ${light ? 'text-[#F4ECDF]' : 'text-[#1C130D]'}`}>
      {title} {accent && <span className="copper-shine font-semibold italic">{accent}</span>}
    </h2>
    {sub && (
      <p className={`font-body text-[15px] leading-[1.65] mt-4 ${light ? 'text-[#F4ECDF]/70' : 'text-[#1C130D]/65'}`}>
        {sub}
      </p>
    )}
  </div>
);

/* ============ NAV ============ */
function Nav() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { label: 'Neden DEO', href: '#neden' },
    { label: 'Özellikler', href: '#ozellikler' },
    { label: 'Modüller', href: '#moduller' },
    { label: 'Fiyatlandırma', href: '#fiyat' },
    { label: 'SSS', href: '#sss' },
  ];

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 transition-all"
      style={{
        background: 'linear-gradient(135deg, #E08A4F 0%, #C56C39 50%, #8E4A23 100%)',
        backdropFilter: scrolled ? 'blur(20px) saturate(140%)' : 'none',
        borderBottom: '1px solid rgba(28, 19, 13, 0.18)',
        boxShadow: scrolled ? '0 8px 24px -8px rgba(0,0,0,0.35)' : 'none',
      }}
      data-testid="landing-nav"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <a href="#top" className="flex items-center group" data-testid="nav-logo">
          <DeoLogo orientation="horizontal" tone="espresso" size={36} />
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map(l => (
            <a
              key={l.href}
              href={l.href}
              className="font-body text-[16px] text-[#1C130D]/85 hover:text-[#1C130D] font-medium transition-colors tracking-tight"
              data-testid={`nav-${l.href.slice(1)}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href="#iletisim" className="hidden sm:inline-flex font-body text-[16px] text-[#1C130D]/85 hover:text-[#1C130D] font-medium transition-colors tracking-tight" data-testid="nav-contact-link">
            Giriş
          </a>
          <a
            href="#iletisim"
            className="inline-flex items-center font-display text-[15px] font-semibold tracking-[0.10em] uppercase rounded-full transition-all hover:scale-[1.03]"
            style={{
              padding: '10px 22px',
              background: 'linear-gradient(135deg, #1C130D 0%, #2B1E16 100%)',
              color: '#F4D5B8',
              border: '1px solid rgba(28,19,13,0.5)',
              boxShadow: '0 1px 0 rgba(244,213,184,0.18) inset, 0 8px 16px -6px rgba(0,0,0,0.4)',
            }}
            data-testid="nav-cta"
          >
            Demo İste
          </a>
        </div>
      </div>
    </header>
  );
}

/* ============ HERO ============ */
function Hero() {
  const [showBubble, setShowBubble] = useState(false);
  const [bubbleClicked, setBubbleClicked] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [autoPlayed, setAutoPlayed] = useState(false);
  const audioRef = React.useRef(null);
  const preloadedRef = React.useRef(null);

  const welcomeText =
    'Ben DEO, emlak ofisinizin dijital zekâsı. Modern emlakçılığın dijital yüzüyle tanışmaya hazır mısın?';

  useEffect(() => {
    const t = setTimeout(() => setShowBubble(true), 2500);
    return () => clearTimeout(t);
  }, []);

  // Pre-fetch TTS audio so it's ready when user first interacts
  useEffect(() => {
    let cancelled = false;
    const preload = async () => {
      try {
        const backend = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${backend}/api/tts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: welcomeText, voice: 'echo', speed: 1.08 }),
        });
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.preload = 'auto';
        audio.onended = () => setSpeaking(false);
        audio.onerror = () => setSpeaking(false);
        preloadedRef.current = audio;
      } catch (e) {
        console.error('TTS preload failed', e);
      }
    };
    preload();
    return () => { cancelled = true; };
  }, []);

  // Auto-play welcome on first user gesture (autoplay policy)
  useEffect(() => {
    if (autoPlayed) return;
    const tryPlay = async () => {
      if (autoPlayed) return;
      const audio = preloadedRef.current;
      if (!audio) return;
      try {
        setSpeaking(true);
        setAutoPlayed(true);
        audioRef.current = audio;
        await audio.play();
      } catch (e) {
        setSpeaking(false);
      }
    };
    const events = ['pointerdown', 'touchstart', 'keydown', 'scroll', 'mousemove'];
    events.forEach(ev => window.addEventListener(ev, tryPlay, { once: true, passive: true }));
    return () => {
      events.forEach(ev => window.removeEventListener(ev, tryPlay));
    };
  }, [autoPlayed]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch (_) {}
      }
    };
  }, []);

  const speakBubble = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (speaking && audioRef.current) {
      try { audioRef.current.pause(); } catch (_) {}
      setSpeaking(false);
      return;
    }
    try {
      setSpeaking(true);
      // Re-use preloaded audio if available
      if (preloadedRef.current) {
        const audio = preloadedRef.current;
        try { audio.currentTime = 0; } catch (_) {}
        audioRef.current = audio;
        await audio.play();
        return;
      }
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: welcomeText, voice: 'echo', speed: 1.08 }),
      });
      if (!res.ok) throw new Error(`TTS ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      audio.onerror = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      await audio.play();
    } catch (err) {
      console.error('TTS error', err);
      setSpeaking(false);
    }
  };

  return (
    <section id="top" className="relative pt-32 pb-24 px-6 lg:px-10 overflow-hidden" data-testid="landing-hero">
      <div className="max-w-[1280px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
        {/* LEFT */}
        <div className="lg:col-span-7 relative z-10">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full"
            style={{
              background: 'rgba(28,19,13,0.05)',
              border: '1px solid rgba(197,108,57,0.3)',
            }}
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#68E5B2] animate-pulse" />
            <span className="font-display text-[11px] tracking-[0.22em] uppercase text-[#8E4A23] font-semibold">
              Türkiye'nin İlk Sesli AI Emlak Ofisi
            </span>
          </div>

          <h1 className="font-display text-[48px] sm:text-[64px] lg:text-[76px] leading-[1.02] font-light text-[#1C130D] tracking-tight">
            Emlak ofisinizin
            <br />
            <span className="copper-shine font-semibold italic">dijital zekâsı.</span>
          </h1>

          <p className="font-body text-[16px] sm:text-[18px] leading-[1.6] text-[#1C130D]/70 mt-7 max-w-[540px]">
            DEO; sesli arama, akıllı eşleştirme, otomatik sunum üretimi ve canlı harita merkezini
            tek bir sade panelde birleştirir. Klavyeye dokunmadan binlerce müşteriyi yönetin.
          </p>

          <div className="flex flex-wrap items-center gap-4 mt-10">
            <a href="#iletisim" className="btn-copper inline-flex items-center gap-2.5 group" data-testid="hero-cta-primary">
              <span>Demo Talep Et</span>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="/uygulama"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-display text-[13px] tracking-[0.16em] uppercase text-[#1C130D] hover:bg-[#1C130D]/5 transition-all"
              style={{ border: '1px solid rgba(28,19,13,0.20)' }}
              data-testid="hero-cta-secondary"
            >
              Paneli Keşfet
              <ChevronDown size={14} />
            </a>
          </div>

          {/* Trust strip — sadece desktop */}
          <div className="hidden lg:flex mt-12 flex-wrap items-center gap-x-8 gap-y-4">
            <Eyebrow>Tercih edenler</Eyebrow>
            <div className="flex items-center gap-x-7 gap-y-2 flex-wrap">
              {['Antalya Realty', 'Bodrum Estates', 'Kalkan Premium', 'Side Vista', 'Kaş Konut'].map(b => (
                <div key={b} className="font-display text-[12px] tracking-[0.18em] text-[#1C130D]/45 uppercase font-medium">
                  {b}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT — sphere preview / keyhole panel (mobilde oval YOK, sadece bot) */}
        <div className="lg:col-span-5 relative h-[420px] sm:h-[460px] lg:h-[520px] block">
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="hero-keyhole-panel relative px-4 lg:px-10 py-4 lg:py-8 flex flex-col items-center"
              data-testid="hero-keyhole-panel"
            >
              <div className="hidden lg:block absolute inset-2 pointer-events-none"
                style={{ border: '1px solid rgba(197,108,57,0.18)', borderRadius: 'clamp(86px, 20vw, 156px)' }} />

              <div className="relative z-10 flex flex-col items-center justify-center pt-0 lg:pt-4 h-full gap-1">
                {/* DEO-Bot küre — mobilde 2x büyük, desktop'ta oval'a sığacak şekilde */}
                <div
                  className="relative hero-bot-sphere"
                  style={{
                    animation: 'bot-idle 8s ease-in-out infinite',
                  }}
                >
                  <div
                    className="absolute inset-0 rounded-full pointer-events-none"
                    style={{
                      background: 'radial-gradient(circle, rgba(197,108,57,0.32) 0%, transparent 65%)',
                      filter: 'blur(24px)',
                      transform: 'scale(1.25)',
                      animation: 'bot-glow 5s ease-in-out infinite',
                    }}
                  />
                  <img
                    src="/assets/brand/deo_bot_clean.png"
                    alt="DEO Bot"
                    className="relative w-full h-full object-contain"
                    style={{
                      mixBlendMode: 'lighten',
                      filter: 'drop-shadow(0 24px 48px rgba(0,0,0,0.75))',
                    }}
                    draggable={false}
                  />
                  {/* Sol göz - yeşil sabit */}
                  <span
                    className="absolute pointer-events-none rounded-full"
                    style={{
                      top: '46%', left: '7%',
                      width: 'clamp(7px, 1.6vw, 11px)', height: 'clamp(7px, 1.6vw, 11px)',
                      transform: 'translateY(-50%)',
                      background: '#68E5B2',
                      animation: 'eye-glow-left 4.5s ease-in-out infinite',
                    }}
                  />
                  {/* Sağ göz - sarı sabit */}
                  <span
                    className="absolute pointer-events-none rounded-full"
                    style={{
                      top: '46%', right: '7%',
                      width: 'clamp(7px, 1.6vw, 11px)', height: 'clamp(7px, 1.6vw, 11px)',
                      transform: 'translateY(-50%)',
                      background: '#F7D002',
                      animation: 'eye-glow-right 4.5s ease-in-out infinite',
                    }}
                  />
                </div>

                {/* DEO başlığı — bot'un hemen altında */}
                <div className="mt-1 text-center">
                  <div className="font-display copper-shine font-bold tracking-[0.45em] text-[22px] sm:text-[24px] lg:text-[26px] uppercase pl-[0.45em]">
                    D E O
                  </div>
                </div>

                {/* Tek satır tagline — sadece desktop'ta, DEO'ya yakın */}
                <div className="hidden lg:block font-display copper-text font-semibold tracking-[0.18em] text-[10px] uppercase text-center mt-1.5 px-4 whitespace-nowrap">
                  EMLAK OFİSİNİZİN DİJİTAL ZEKÂSI
                </div>
              </div>
            </div>
          </div>

          {/* Floating callout chips — küçük ekranda gizle */}
          <div className="absolute top-12 -left-4 px-3 py-2 rounded-xl hidden md:block"
            style={{
              background: 'rgba(244, 236, 223, 0.92)',
              border: '1px solid rgba(197, 108, 57, 0.35)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 12px 32px -8px rgba(0,0,0,0.25)',
            }}
            data-testid="hero-chip-1"
          >
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #E08A4F, #C56C39)' }}>
                <Mic size={14} className="text-[#1C130D]" />
              </div>
              <div>
                <div className="font-body text-[8px] tracking-[0.24em] uppercase text-[#8E4A23] font-semibold">Sesli Arama</div>
                <div className="font-display text-[11px] text-[#1C130D] font-medium">"Lara'da deniz manzaralı 4+1"</div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-20 -right-2 px-3 py-2 rounded-xl hidden md:block"
            style={{
              background: 'rgba(244, 236, 223, 0.92)',
              border: '1px solid rgba(104, 229, 178, 0.45)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 12px 32px -8px rgba(0,0,0,0.25)',
            }}
            data-testid="hero-chip-2"
          >
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ background: 'rgba(104, 229, 178, 0.18)', border: '1px solid rgba(104, 229, 178, 0.4)' }}>
                <Sparkles size={14} className="text-[#2A9669]" />
              </div>
              <div>
                <div className="font-body text-[8px] tracking-[0.24em] uppercase text-[#2A9669] font-semibold">12 Eşleşme</div>
                <div className="font-display text-[11px] text-[#1C130D] font-medium">Otomatik bulundu</div>
              </div>
            </div>
          </div>

          {/* DEO-Bot mikro etkileşim — konuşma balonu */}
          {showBubble && !bubbleClicked && (
            <div className="absolute z-30 left-0 right-0 lg:left-auto lg:right-[-28px] bottom-1 lg:bottom-auto lg:top-[40px] flex justify-center lg:block pointer-events-none">
              <a
                href="#iletisim"
                onClick={() => setBubbleClicked(true)}
                className="group cursor-pointer pointer-events-auto"
                style={{
                  animation: 'bubble-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both',
                  width: 'min(72vw, 260px)',
                }}
                data-testid="hero-bot-bubble"
              >
              <div className="relative px-3.5 py-2.5 lg:px-5 lg:py-4 rounded-2xl w-full lg:max-w-[260px]"
                style={{
                  background: 'linear-gradient(135deg, #1C130D 0%, #2B1E16 100%)',
                  border: '1px solid rgba(224, 138, 79, 0.55)',
                  boxShadow: '0 18px 48px -12px rgba(0,0,0,0.55), 0 0 24px -8px rgba(224,138,79,0.4)',
                }}
              >
                <div className="font-body text-[8px] lg:text-[9px] tracking-[0.24em] uppercase text-[#E08A4F] font-semibold mb-1">
                  Deo-Bot
                </div>
                <div className="font-display text-[10.5px] lg:text-[12.5px] text-[#F4ECDF] leading-[1.5] font-light">
                  <span className="copper-shine font-semibold italic">Ben DEO,</span> emlak ofisinizin dijital zekâsı. Modern emlakçılığın <span className="copper-shine font-semibold italic">dijital yüzüyle</span> tanışmaya hazır mısın?
                </div>
                <div className="mt-1.5 lg:mt-3 flex items-center justify-between gap-2">
                  <span className="font-body text-[8.5px] lg:text-[10px] tracking-[0.16em] uppercase text-[#68E5B2] font-semibold group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-1">
                    Evet, başla <ArrowRight size={10} />
                  </span>
                  <button
                    type="button"
                    onClick={speakBubble}
                    className="inline-flex items-center justify-center rounded-full transition-opacity hover:opacity-80 w-[20px] h-[20px] lg:w-[24px] lg:h-[24px]"
                    style={{
                      background: 'rgba(247, 208, 2, 0.14)',
                      border: '1px solid rgba(247, 208, 2, 0.45)',
                      color: '#F7D002',
                    }}
                    title={speaking ? 'Sesi durdur' : 'Sesle dinle'}
                    data-testid="hero-bot-tts"
                  >
                    {speaking ? (
                      <VolumeX size={10} />
                    ) : (
                      <Volume2 size={10} />
                    )}
                  </button>
                </div>
                {/* Sol ok — sadece desktop */}
                <div className="hidden lg:block absolute"
                  style={{
                    left: '-9px', top: '22px',
                    width: 0, height: 0,
                    borderTop: '8px solid transparent',
                    borderBottom: '8px solid transparent',
                    borderRight: '10px solid #1C130D',
                  }}
                />
              </div>
            </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ============ STATS ============ */
function Stats() {
  const stats = [
    { num: '23.335', label: 'Aktif Müşteri', delta: '+1.842' },
    { num: '423', label: 'Açık Portföy', delta: '+38' },
    { num: '%38', label: 'Dönüşüm Artışı', delta: 'Q4 ortalaması' },
    { num: '6', label: 'Şehir', delta: 'Antalya · İzmir · Bodrum' },
  ];

  return (
    <section className="px-6 lg:px-10 -mt-8 relative z-20" data-testid="stats-section">
      <div className="max-w-[1280px] mx-auto">
        <div
          className="rounded-3xl px-6 sm:px-10 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8"
          style={{
            background: 'linear-gradient(135deg, rgba(15,8,4,0.94), rgba(28,19,13,0.88))',
            border: '1px solid rgba(197, 108, 57, 0.40)',
            boxShadow: '0 1px 0 rgba(244,213,184,0.12) inset, 0 28px 56px -16px rgba(0,0,0,0.45)',
          }}
        >
          {stats.map((s, i) => (
            <div
              key={s.label}
              className={`${i > 0 ? 'lg:border-l lg:border-white/8 lg:pl-8' : ''}`}
              data-testid={`stat-${i}`}
            >
              <div className="font-display text-[40px] sm:text-[48px] copper-shine font-bold leading-none tracking-tight">
                {s.num}
              </div>
              <div className="font-body text-[10px] tracking-[0.32em] uppercase text-[#F4ECDF]/55 font-semibold mt-3">
                {s.label}
              </div>
              <div className="font-body text-[11px] text-[#68E5B2] mt-1.5 tracking-tight">
                {s.delta}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ NEDEN DEO ============ */
function WhyDeo() {
  const items = [
    { icon: Mic, title: 'Sesli Komut', desc: 'Klavye yerine sesli arama. Müşteri profili, ilçe, oda sayısı söyleyin; DEO eşleştirsin.' },
    { icon: Sparkles, title: 'AI Eşleştirme', desc: 'Müşteri talebini portföyünüzle saniyede eşleştirir; sıralı en iyi 5 öneriyi gösterir.' },
    { icon: FileText, title: 'Otomatik Sunum', desc: 'Tek tıkla markalı PDF sunum. Logonuz, fotoğraflarınız, fiyat tabloları hazır.' },
    { icon: Map, title: 'Canlı Harita', desc: 'Antalya ilçeleri ve bölge analizi tek panelde. Sıcak bölgeleri anında görün.' },
    { icon: Workflow, title: 'CRM Entegrasyonu', desc: 'WhatsApp, Sahibinden, Hürriyet Emlak — tüm taleplerin otomatik akışı.' },
    { icon: Shield, title: 'KVKK Uyumlu', desc: 'Müşteri verisi şifrelenmiş kasada. Türkiye sunucularında saklanır.' },
  ];
  return (
    <section id="neden" className="px-6 lg:px-10 py-28" data-testid="why-section">
      <div className="max-w-[1280px] mx-auto">
        <SectionHead
          eyebrow="Neden DEO"
          title="Neden binlerce ofis"
          accent="bizi seçiyor?"
          sub="Klasik CRM'ler size form doldurtur. DEO size konuşur — ve sizinle birlikte kazanır."
        />

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <div
                key={it.title}
                className="rounded-2xl p-7 transition-all hover:-translate-y-1 group"
                style={{
                  background: 'linear-gradient(135deg, rgba(244,236,223,0.96), rgba(232,222,205,0.92))',
                  border: '1px solid rgba(197,108,57,0.25)',
                  boxShadow: '0 1px 0 rgba(255,255,255,0.6) inset, 0 16px 36px -12px rgba(0,0,0,0.18)',
                }}
                data-testid={`why-card-${i}`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform"
                  style={{
                    background: 'linear-gradient(135deg, rgba(224,138,79,0.18), rgba(197,108,57,0.08))',
                    border: '1px solid rgba(197,108,57,0.3)',
                  }}
                >
                  <Icon size={20} className="text-[#8E4A23]" />
                </div>
                <h3 className="font-display text-[18px] font-semibold text-[#1C130D] tracking-tight mb-2">
                  {it.title}
                </h3>
                <p className="font-body text-[13.5px] leading-[1.6] text-[#1C130D]/65">
                  {it.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============ SHOWCASE / DASHBOARD PREVIEW ============ */
function Showcase() {
  return (
    <section id="showcase" className="px-6 lg:px-10 py-24 relative" data-testid="showcase-section">
      <div className="max-w-[1280px] mx-auto">
        <div className="text-center mb-10">
          <Eyebrow>Panel Önizleme</Eyebrow>
          <h2 className="font-display text-[40px] sm:text-[48px] leading-[1.1] font-light tracking-tight mt-3 text-[#1C130D]">
            Tek panelden <span className="copper-shine font-semibold italic">tüm süreç.</span>
          </h2>
        </div>

        {/* Mocked dashboard frame */}
        <div
          className="relative rounded-3xl p-3 mx-auto max-w-[1100px]"
          style={{
            background: 'linear-gradient(135deg, rgba(28,19,13,0.92), rgba(15,8,4,0.88))',
            border: '1px solid rgba(197,108,57,0.4)',
            boxShadow: '0 1px 0 rgba(244,213,184,0.12) inset, 0 32px 64px -20px rgba(0,0,0,0.5)',
          }}
        >
          {/* Window dots */}
          <div className="flex items-center gap-1.5 px-3 py-2.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
            <div className="ml-4 font-body text-[9px] text-[#F4ECDF]/35 tracking-[0.32em] uppercase">
              deo.ofis · panel
            </div>
          </div>

          <div
            className="rounded-2xl overflow-hidden grid grid-cols-12 gap-3 p-5"
            style={{ background: pageBg.background, minHeight: '520px' }}
          >
            {/* Sidebar mock */}
            <div className="col-span-1 flex flex-col items-center gap-2 py-2">
              {[BarChart3, Building2, Users, Sparkles, Heart, Map].map((Icn, i) => (
                <div
                  key={i}
                  className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{
                    background: i === 0 ? 'linear-gradient(135deg, #E08A4F, #C56C39)' : 'rgba(28,19,13,0.05)',
                    border: i === 0 ? '1px solid rgba(244,213,184,0.4)' : '1px solid rgba(28,19,13,0.08)',
                  }}
                >
                  <Icn size={14} className={i === 0 ? 'text-[#1C130D]' : 'text-[#5A2F12]/60'} />
                </div>
              ))}
            </div>

            {/* Content mock */}
            <div className="col-span-11 space-y-4">
              <div>
                <Eyebrow>Genel Bakış</Eyebrow>
                <div className="font-display text-[26px] text-[#1C130D] font-light mt-1.5">
                  Selam <span className="copper-shine font-semibold">Mehmet K.,</span>{' '}
                  <span className="font-light">Hadi biraz</span>{' '}
                  <span className="copper-shine font-semibold italic">kazanalım..</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {[
                  { v: '23.335', l: 'Toplam Müşteri', d: '+1.842' },
                  { v: '423', l: 'Açık Portföy', d: '+38' },
                  { v: '127', l: 'Açılan (bu ay)', d: '+22%' },
                ].map((c) => (
                  <div key={c.l} className="rounded-xl p-4"
                    style={{
                      background: 'rgba(244,236,223,0.85)',
                      border: '1px solid rgba(197,108,57,0.25)',
                    }}
                  >
                    <div className="font-body text-[8px] tracking-[0.28em] text-[#5A2F12]/65 uppercase">{c.l}</div>
                    <div className="flex items-baseline gap-2 mt-1.5">
                      <div className="font-display text-[22px] font-semibold text-[#1C130D]">{c.v}</div>
                      <div className="font-body text-[10px] text-[#2A9669] font-semibold">{c.d}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-3">
                {[
                  { name: 'Konyaaltı', pct: 100, val: '₺1.2B' },
                  { name: 'Lara', pct: 80, val: '₺980M' },
                  { name: 'Kalkan', pct: 60, val: '₺1.6B' },
                ].map(d => (
                  <div key={d.name} className="rounded-xl p-3 flex items-center gap-3"
                    style={{
                      background: 'rgba(15,8,4,0.92)',
                      border: '1px solid rgba(197,108,57,0.35)',
                    }}
                  >
                    <div className="flex-1">
                      <div className="font-display text-[12px] text-[#F4ECDF] font-medium">{d.name}</div>
                      <div className="h-1 rounded-full mt-1.5" style={{ background: 'rgba(244,213,184,0.15)' }}>
                        <div className="h-full rounded-full" style={{ width: `${d.pct}%`, background: 'linear-gradient(90deg,#E08A4F,#C56C39)' }} />
                      </div>
                    </div>
                    <div className="font-display text-[12px] copper-shine font-semibold">{d.val}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-10">
          <Link to="/uygulama" className="btn-copper inline-flex items-center gap-2.5 group" data-testid="showcase-cta">
            <span>Canlı Paneli Aç</span>
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ============ AI ÖZELLİKLERİ ============ */
function AIFeatures() {
  const features = [
    {
      icon: Mic,
      tag: 'Sesli Arama',
      title: 'Konuşun, DEO bulsun.',
      desc: '"Antalya Lara\'da 4+1, deniz manzaralı, 5M altında" deyin; sonuçları saniyede önünüze getirsin.',
      tone: 'copper',
    },
    {
      icon: Sparkles,
      tag: 'Akıllı Eşleştirme',
      title: 'Müşteri ↔ Portföy.',
      desc: 'Bütçe, yaşam tarzı, lokasyon ve geçmiş etkileşim verisini birleştirip en doğru eşleşmeyi sıralar.',
      tone: 'mint',
    },
    {
      icon: FileText,
      tag: 'Sunum Üretimi',
      title: 'Marka renginizde, anında.',
      desc: 'Logo, fotoğraf, fiyat tablosu, harita, AI özetlerle 16 sayfalık satış sunumunu 30 saniyede oluşturun.',
      tone: 'yellow',
    },
    {
      icon: BarChart3,
      tag: 'Tahmin Motoru',
      title: 'Bölge fiyat eğilimi.',
      desc: 'Her ilçe için 90 günlük fiyat trend tahmini, satış olasılığı ve tipik müşteri profili.',
      tone: 'copper',
    },
  ];

  const toneStyles = {
    copper: { c: '#E08A4F', bg: 'rgba(197,108,57,0.18)', bd: 'rgba(197,108,57,0.4)' },
    mint:   { c: '#68E5B2', bg: 'rgba(104,229,178,0.15)', bd: 'rgba(104,229,178,0.4)' },
    yellow: { c: '#F7D002', bg: 'rgba(247,208,2,0.15)',  bd: 'rgba(247,208,2,0.4)' },
  };

  return (
    <section id="ozellikler" className="px-6 lg:px-10 py-28 relative"
      style={{
        background: '#1C130D',
      }}
      data-testid="ai-features-section"
    >
      <div className="max-w-[1280px] mx-auto">
        <SectionHead
          eyebrow="AI Yetenekleri"
          title="Gerçek zamanlı"
          accent="yapay zekâ."
          sub="DEO sadece bir CRM değil — saatte 1.200 kararı sizin yerinize işleyen, öğrenen bir motor."
          light
        />

        <div className="grid lg:grid-cols-2 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            const t = toneStyles[f.tone];
            return (
              <div
                key={f.title}
                className="rounded-3xl p-8 relative overflow-hidden group"
                style={{
                  background: 'linear-gradient(135deg, rgba(28,19,13,0.7), rgba(15,8,4,0.55))',
                  backdropFilter: 'blur(16px)',
                  border: '1px solid rgba(197,108,57,0.30)',
                  boxShadow: '0 1px 0 rgba(244,213,184,0.08) inset, 0 24px 48px -16px rgba(0,0,0,0.45)',
                }}
                data-testid={`ai-feature-${i}`}
              >
                {/* glow blob */}
                <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-30 group-hover:opacity-50 transition-opacity"
                  style={{ background: `radial-gradient(circle, ${t.c}, transparent 70%)`, filter: 'blur(40px)' }}
                />

                <div className="relative">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{ background: t.bg, border: `1px solid ${t.bd}` }}
                    >
                      <Icon size={20} style={{ color: t.c }} />
                    </div>
                    <div className="font-body text-[10px] tracking-[0.32em] uppercase font-semibold" style={{ color: t.c }}>
                      {f.tag}
                    </div>
                  </div>
                  <h3 className="font-display text-[28px] text-[#F4ECDF] font-light tracking-tight mb-3">
                    {f.title}
                  </h3>
                  <p className="font-body text-[14.5px] leading-[1.65] text-[#F4ECDF]/65 max-w-[440px]">
                    {f.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============ MODÜLLER ============ */
function Modules() {
  const mods = [
    { icon: BarChart3, name: 'Genel Bakış', desc: 'Tüm KPI tek ekranda' },
    { icon: Building2, name: 'Portföy', desc: 'İlan kütüphanesi & filtre' },
    { icon: Users, name: 'Müşteriler', desc: 'CRM ve etkileşim akışı' },
    { icon: Sparkles, name: 'Akıllı Eşleştirme', desc: 'AI portföy-müşteri eşleştirme' },
    { icon: Heart, name: 'Favoriler', desc: 'Müşterinin beğeni geçmişi' },
    { icon: Map, name: 'Harita', desc: 'Bölge analizi & ısı haritası' },
    { icon: Inbox, name: 'Talep Havuzu', desc: 'Açık talep + otomatik tetik' },
    { icon: Plug, name: 'Entegrasyonlar', desc: 'WhatsApp · Sahibinden · Hürriyet' },
  ];
  return (
    <section id="moduller" className="px-6 lg:px-10 py-28" data-testid="modules-section">
      <div className="max-w-[1280px] mx-auto">
        <SectionHead
          eyebrow="Modüller"
          title="Sekiz modül,"
          accent="tek panel."
          sub="Her modül birbirini besliyor; tekrar veri girmeniz gerekmiyor."
        />

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {mods.map((m, i) => {
            const Icon = m.icon;
            return (
              <div
                key={m.name}
                className="rounded-2xl p-6 group transition-all hover:-translate-y-1 cursor-default"
                style={{
                  background: 'linear-gradient(135deg, #2B1E16, #1C130D)',
                  border: '1px solid rgba(197,108,57,0.40)',
                  boxShadow: '0 1px 0 rgba(244,213,184,0.10) inset, 0 16px 32px -12px rgba(0,0,0,0.35)',
                }}
                data-testid={`module-${i}`}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(224,138,79,0.25), rgba(197,108,57,0.12))',
                      border: '1px solid rgba(197,108,57,0.45)',
                    }}
                  >
                    <Icon size={18} className="text-[#E08A4F]" />
                  </div>
                  <span className="font-body text-[9px] tracking-[0.22em] text-[#E08A4F]/60 uppercase font-semibold">
                    0{i + 1}
                  </span>
                </div>
                <div className="font-display text-[15px] font-semibold text-[#F4ECDF] tracking-tight mb-1">
                  {m.name}
                </div>
                <div className="font-body text-[12px] text-[#F4ECDF]/65 leading-snug">
                  {m.desc}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============ BÖLGELER ============ */
function Regions() {
  const regions = [
    { name: 'Antalya · Konyaaltı', count: 64, change: '+12%', val: '₺ 1.2B' },
    { name: 'Antalya · Lara',      count: 52, change: '+8%',  val: '₺ 980M' },
    { name: 'Antalya · Kalkan',    count: 38, change: '+22%', val: '₺ 1.6B' },
    { name: 'Antalya · Belek',     count: 29, change: '+5%',  val: '₺ 540M' },
    { name: 'Bodrum · Yalıkavak',  count: 41, change: '+18%', val: '₺ 1.4B' },
    { name: 'İzmir · Çeşme',       count: 33, change: '+14%', val: '₺ 870M' },
  ];
  return (
    <section className="px-6 lg:px-10 py-28" data-testid="regions-section">
      <div className="max-w-[1280px] mx-auto">
        <SectionHead
          eyebrow="Aktif Bölgeler"
          title="Türkiye'nin lüks koridorlarında"
          accent="canlı veri."
        />

        <div
          className="rounded-3xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(244,236,223,0.95), rgba(232,222,205,0.88))',
            border: '1px solid rgba(197,108,57,0.25)',
            boxShadow: '0 1px 0 rgba(255,255,255,0.5) inset, 0 24px 48px -16px rgba(0,0,0,0.2)',
          }}
        >
          <div className="grid grid-cols-12 px-6 py-4 border-b border-[#1C130D]/10">
            <div className="col-span-1"><Eyebrow>#</Eyebrow></div>
            <div className="col-span-4"><Eyebrow>Bölge</Eyebrow></div>
            <div className="col-span-4"><Eyebrow>İlan Yoğunluğu</Eyebrow></div>
            <div className="col-span-2"><Eyebrow>Toplam Değer</Eyebrow></div>
            <div className="col-span-1 text-right"><Eyebrow>Trend</Eyebrow></div>
          </div>
          {regions.map((r, i) => (
            <div key={r.name} className="grid grid-cols-12 items-center px-6 py-4 border-b border-[#1C130D]/8 last:border-0 hover:bg-[#C56C39]/5 transition-colors"
              data-testid={`region-${i}`}
            >
              <div className="col-span-1 font-body text-[11px] text-[#8E4A23]/70 font-semibold">{String(i + 1).padStart(2, '0')}</div>
              <div className="col-span-4 font-display text-[14px] text-[#1C130D] font-medium tracking-tight">{r.name}</div>
              <div className="col-span-4 flex items-center gap-3">
                <div className="flex-1 h-1.5 rounded-full" style={{ background: 'rgba(28,19,13,0.08)' }}>
                  <div className="h-full rounded-full" style={{ width: `${(r.count / 64) * 100}%`, background: 'linear-gradient(90deg,#E08A4F,#C56C39)' }} />
                </div>
                <div className="font-body text-[11px] text-[#1C130D]/55 w-16">{r.count} ilan</div>
              </div>
              <div className="col-span-2 font-display text-[15px] font-bold tracking-tight" style={{
                background: 'linear-gradient(135deg, #C56C39 0%, #E08A4F 50%, #8E4A23 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 1px 0 rgba(255,255,255,0.2)',
              }}>{r.val}</div>
              <div className="col-span-1 text-right font-body text-[11px] text-[#2A9669] font-semibold">{r.change}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ TESTIMONIALS ============ */
function Testimonials() {
  const items = [
    {
      quote: 'Müşteri talebini sesle söylüyoruz, DEO 5 saniyede portföyden eşleşeni getiriyor. Sunumu 30 saniyede gönderiyoruz. Üç ayda satış %42 arttı.',
      name: 'Hakan Yıldız',
      role: 'Genel Müdür · Antalya Realty',
      city: 'Antalya',
    },
    {
      quote: 'Bodrum gibi sezona bağlı bir pazarda canlı bölge analizi her şeyi değiştirdi. Hangi mahallede sıcak fiyat var anında görüyoruz.',
      name: 'Selin Aksoy',
      role: 'Kurucu Ortak · Bodrum Estates',
      city: 'Bodrum',
    },
    {
      quote: 'WhatsApp\'tan gelen talepler otomatik talep havuzuna düşüyor, sonra eşleşmiyle birlikte tek panelde. Acentalarımın gününü 3 saat kısalttık.',
      name: 'Mert Kavaklı',
      role: 'Operasyon Direktörü · Kalkan Premium',
      city: 'Kalkan',
    },
  ];
  return (
    <section className="px-6 lg:px-10 py-28" data-testid="testimonials-section">
      <div className="max-w-[1280px] mx-auto">
        <SectionHead
          eyebrow="Referanslar"
          title="Saha ekipleri"
          accent="ne diyor?"
        />
        <div className="grid lg:grid-cols-3 gap-5">
          {items.map((t, i) => (
            <div
              key={i}
              className="rounded-3xl p-7 relative"
              style={{
                background: 'linear-gradient(135deg, rgba(244,236,223,0.95), rgba(232,222,205,0.88))',
                border: '1px solid rgba(197,108,57,0.25)',
                boxShadow: '0 1px 0 rgba(255,255,255,0.5) inset, 0 16px 32px -12px rgba(0,0,0,0.18)',
              }}
              data-testid={`testimonial-${i}`}
            >
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, k) => (
                  <Star key={k} size={13} className="fill-[#C56C39] text-[#C56C39]" />
                ))}
              </div>
              <p className="font-display text-[15px] leading-[1.6] text-[#1C130D]/85 font-light italic mb-6">
                "{t.quote}"
              </p>
              <div className="flex items-center gap-3 pt-5 border-t border-[#1C130D]/10">
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-display font-semibold text-[#1C130D]"
                  style={{ background: 'linear-gradient(135deg, #E08A4F, #C56C39)' }}
                >
                  {t.name.split(' ').map(s => s[0]).join('')}
                </div>
                <div>
                  <div className="font-display text-[13px] text-[#1C130D] font-semibold">{t.name}</div>
                  <div className="font-body text-[9px] tracking-[0.24em] uppercase text-[#8E4A23]/70">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ FİYATLANDIRMA ============ */
function Pricing() {
  const plans = [
    {
      name: 'Başlangıç',
      price: '₺ 2.490',
      cycle: '/ ay',
      desc: 'Tek ofisli butik acentalar için.',
      cta: 'Demo İste',
      features: ['1 ofis · 3 danışman', '500 müşteri kaydı', '100 portföy ilanı', 'Sesli arama (sınırlı)', 'Standart sunum üretici', 'E-posta destek'],
      highlight: false,
    },
    {
      name: 'Profesyonel',
      price: '₺ 5.990',
      cycle: '/ ay',
      desc: 'Büyüyen ofisler için en popüler tercih.',
      cta: 'Demo İste',
      features: ['Sınırsız ofis & danışman', 'Sınırsız müşteri', 'Sınırsız portföy', 'Tam sesli AI motor', 'Marka kimlikli sunum', 'Akıllı eşleştirme', 'Canlı bölge analizi', '7/24 öncelikli destek'],
      highlight: true,
    },
    {
      name: 'Kurumsal',
      price: 'Özel',
      cycle: 'fiyatlandırma',
      desc: 'Holding ve çok şubeli yapılar için.',
      cta: 'Görüşelim',
      features: ['Özel SLA & SSO', 'Beyaz etiket panel', 'Özel AI eğitimi', 'API & webhook', 'KVKK + ISO uyum raporu', 'Yerinde eğitim & lansman'],
      highlight: false,
    },
  ];
  return (
    <section id="fiyat" className="px-6 lg:px-10 py-28" data-testid="pricing-section">
      <div className="max-w-[1280px] mx-auto">
        <SectionHead
          eyebrow="Fiyatlandırma"
          title="Şeffaf, ofis büyüklüğüne"
          accent="göre planlar."
          sub="14 gün ücretsiz deneme. Kredi kartı gerekmez. İstediğiniz zaman iptal."
          center
        />

        <div className="grid lg:grid-cols-3 gap-5">
          {plans.map((p, i) => (
            <div
              key={p.name}
              className={`rounded-3xl p-8 relative ${p.highlight ? 'lg:-mt-4 lg:mb-4' : ''}`}
              style={
                p.highlight
                  ? {
                      background: 'linear-gradient(135deg, rgba(15,8,4,0.95), rgba(28,19,13,0.92))',
                      border: '1px solid rgba(197,108,57,0.5)',
                      boxShadow: '0 1px 0 rgba(244,213,184,0.18) inset, 0 28px 56px -16px rgba(0,0,0,0.55)',
                    }
                  : {
                      background: 'linear-gradient(135deg, rgba(244,236,223,0.96), rgba(232,222,205,0.88))',
                      border: '1px solid rgba(197,108,57,0.22)',
                      boxShadow: '0 1px 0 rgba(255,255,255,0.5) inset, 0 16px 32px -12px rgba(0,0,0,0.15)',
                    }
              }
              data-testid={`plan-${i}`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full"
                  style={{ background: 'linear-gradient(135deg,#E08A4F,#C56C39)', boxShadow: '0 8px 16px -4px rgba(197,108,57,0.5)' }}
                >
                  <span className="font-body text-[9px] tracking-[0.32em] uppercase text-[#1C130D] font-bold">En Popüler</span>
                </div>
              )}
              <div className="font-display text-[20px] font-semibold tracking-tight mb-2"
                style={{ color: p.highlight ? '#F4ECDF' : '#1C130D' }}
              >
                {p.name}
              </div>
              <p className="font-body text-[12.5px] leading-snug mb-5"
                style={{ color: p.highlight ? 'rgba(244,236,223,0.6)' : 'rgba(28,19,13,0.55)' }}
              >
                {p.desc}
              </p>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="font-display text-[36px] font-bold tracking-tight"
                  style={{ color: p.highlight ? '#F4ECDF' : '#1C130D' }}
                >
                  {p.price}
                </span>
                <span className="font-body text-[11px] tracking-[0.18em] uppercase"
                  style={{ color: p.highlight ? 'rgba(244,236,223,0.55)' : 'rgba(28,19,13,0.5)' }}
                >
                  {p.cycle}
                </span>
              </div>
              <a
                href="#iletisim"
                className={`block text-center mb-7 py-3 rounded-full font-display text-[12px] tracking-[0.18em] uppercase font-semibold transition-all hover:scale-[1.02]`}
                style={
                  p.highlight
                    ? { background: 'linear-gradient(135deg,#E08A4F,#C56C39 50%,#8E4A23)', color: '#1C130D', border: '1px solid rgba(244,213,184,0.4)', boxShadow: '0 1px 0 rgba(244,213,184,0.5) inset' }
                    : { background: 'rgba(28,19,13,0.06)', color: '#1C130D', border: '1px solid rgba(28,19,13,0.20)' }
                }
                data-testid={`plan-cta-${i}`}
              >
                {p.cta}
              </a>
              <ul className="space-y-2.5">
                {p.features.map(f => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check size={14} className="mt-0.5 flex-shrink-0"
                      style={{ color: p.highlight ? '#68E5B2' : '#8E4A23' }}
                    />
                    <span className="font-body text-[13px] leading-snug"
                      style={{ color: p.highlight ? 'rgba(244,236,223,0.85)' : 'rgba(28,19,13,0.78)' }}
                    >
                      {f}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ SSS ============ */
function FAQ() {
  const items = [
    { q: 'Mevcut CRM verilerimi DEO\'ya nasıl aktarırım?', a: 'CSV/Excel yükleme, Google Sheets bağlantısı veya API ile saatler içinde aktarım yaparız. Lansman ekibimiz sıfır veri kaybıyla taşımayı garanti eder.' },
    { q: 'Sesli arama hangi dilleri destekliyor?', a: 'Tam Türkçe (lehçe varyasyonlarıyla) ve İngilizce. Almanca ve Rusça desteği yatırım için sezgisel komutlarla sınırlı şekilde mevcuttur.' },
    { q: 'Verilerim nerede saklanıyor?', a: 'Tüm müşteri ve ilan verisi Türkiye lokasyonundaki sunucularımızda KVKK ve ISO 27001 standartlarında şifrelenmiş olarak saklanır.' },
    { q: 'Sahibinden, Hürriyet Emlak gibi siteler ile entegre mi?', a: 'Evet — ilanlarınızı tek tıkla yayınlar, gelen aramaları talep havuzuna otomatik düşürür. WhatsApp Business da entegredir.' },
    { q: 'Aylık taahhüt zorunlu mu?', a: 'Hayır. 14 gün ücretsiz deneme sonrası dilediğiniz zaman iptal edebilirsiniz. Yıllık ödemede %18 indirim sunuyoruz.' },
    { q: 'Eğitim ve destek nasıl ilerliyor?', a: 'Profesyonel ve Kurumsal planda ofisinizde 1 günlük başlangıç eğitimi + sınırsız uzaktan destek. Tüm planlarda zengin video kütüphanesi mevcuttur.' },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section id="sss" className="px-6 lg:px-10 py-28" data-testid="faq-section">
      <div className="max-w-[920px] mx-auto">
        <SectionHead
          eyebrow="Sıkça Sorulanlar"
          title="Cevaplar"
          accent="hazır."
          center
        />
        <div className="space-y-3">
          {items.map((it, i) => (
            <div
              key={i}
              className="rounded-2xl overflow-hidden transition-all"
              style={{
                background: open === i ? 'linear-gradient(135deg, rgba(244,236,223,0.95), rgba(232,222,205,0.88))' : 'rgba(244,236,223,0.7)',
                border: open === i ? '1px solid rgba(197,108,57,0.4)' : '1px solid rgba(197,108,57,0.18)',
                boxShadow: open === i ? '0 16px 32px -12px rgba(0,0,0,0.18)' : 'none',
              }}
              data-testid={`faq-${i}`}
            >
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between text-left px-6 py-5 hover:bg-[#C56C39]/5 transition-colors"
                data-testid={`faq-toggle-${i}`}
              >
                <span className="font-display text-[15px] text-[#1C130D] font-medium tracking-tight pr-4">{it.q}</span>
                <ChevronDown
                  size={18}
                  className="text-[#8E4A23] flex-shrink-0 transition-transform"
                  style={{ transform: open === i ? 'rotate(180deg)' : 'rotate(0)' }}
                />
              </button>
              {open === i && (
                <div className="px-6 pb-5">
                  <div className="h-px bg-[#1C130D]/10 mb-4" />
                  <p className="font-body text-[13.5px] leading-[1.7] text-[#1C130D]/70">{it.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ İLETİŞİM FORMU ============ */
function ContactForm() {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', size: '1-3', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => setForm(s => ({ ...s, [k]: e.target.value }));
  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  return (
    <section id="iletisim" className="px-6 lg:px-10 py-28 relative" data-testid="contact-section">
      <div className="max-w-[1080px] mx-auto">
        <div
          className="rounded-3xl overflow-hidden grid lg:grid-cols-12"
          style={{
            background: 'linear-gradient(135deg, rgba(15,8,4,0.95), rgba(28,19,13,0.90))',
            border: '1px solid rgba(197,108,57,0.45)',
            boxShadow: '0 1px 0 rgba(244,213,184,0.12) inset, 0 32px 64px -16px rgba(0,0,0,0.5)',
          }}
        >
          {/* Left info pane */}
          <div className="lg:col-span-5 p-10 relative"
            style={{
              background: 'radial-gradient(ellipse at 30% 0%, rgba(197,108,57,0.18), transparent 60%)',
              borderRight: '1px solid rgba(197,108,57,0.25)',
            }}
          >
            <Eyebrow light>Demo Talebi</Eyebrow>
            <h2 className="font-display text-[34px] font-light text-[#F4ECDF] tracking-tight mt-3 leading-[1.1]">
              30 dakikalık demo, ofisinize <span className="copper-shine font-semibold italic">özel kurgu.</span>
            </h2>
            <p className="font-body text-[14px] text-[#F4ECDF]/65 leading-[1.65] mt-5">
              Formu doldurun; uzmanlarımız 24 saat içinde sizinle iletişime geçsin, ofisinize özel bir DEO senaryosu hazırlayalım.
            </p>

            <div className="mt-10 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ background: 'rgba(197,108,57,0.18)', border: '1px solid rgba(197,108,57,0.3)' }}
                >
                  <Phone size={14} className="text-[#E08A4F]" />
                </div>
                <div>
                  <Eyebrow light>Telefon</Eyebrow>
                  <div className="font-display text-[14px] text-[#F4ECDF] mt-1">+90 (242) 000 00 00</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ background: 'rgba(197,108,57,0.18)', border: '1px solid rgba(197,108,57,0.3)' }}
                >
                  <Mail size={14} className="text-[#E08A4F]" />
                </div>
                <div>
                  <Eyebrow light>E-posta</Eyebrow>
                  <div className="font-display text-[14px] text-[#F4ECDF] mt-1">demo@deo.ofis</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ background: 'rgba(197,108,57,0.18)', border: '1px solid rgba(197,108,57,0.3)' }}
                >
                  <MapPin size={14} className="text-[#E08A4F]" />
                </div>
                <div>
                  <Eyebrow light>Merkez</Eyebrow>
                  <div className="font-display text-[14px] text-[#F4ECDF] mt-1">Antalya · Konyaaltı</div>
                </div>
              </div>
            </div>

            <div className="mt-10 flex items-center gap-3 pt-6 border-t border-white/8">
              <BadgeCheck size={16} className="text-[#68E5B2]" />
              <div className="font-body text-[10px] tracking-[0.28em] uppercase text-[#68E5B2] font-semibold">
                14 gün ücretsiz · KVKK uyumlu
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7 p-10">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-20" data-testid="contact-success">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
                  style={{ background: 'rgba(104,229,178,0.15)', border: '1px solid rgba(104,229,178,0.4)' }}
                >
                  <Check size={28} className="text-[#68E5B2]" />
                </div>
                <h3 className="font-display text-[24px] text-[#F4ECDF] font-light tracking-tight mb-2">
                  Talebiniz alındı.
                </h3>
                <p className="font-body text-[13px] text-[#F4ECDF]/65 max-w-[360px]">
                  Uzmanlarımız 24 saat içinde size dönüş yapacak. Bu arada paneli incelemeye başlayabilirsiniz.
                </p>
                <Link to="/uygulama" className="btn-copper mt-6 inline-flex items-center gap-2" data-testid="contact-success-cta">
                  Paneli Aç <ArrowRight size={14} />
                </Link>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Ad Soyad" testid="field-name">
                    <input
                      required
                      type="text"
                      value={form.name}
                      onChange={onChange('name')}
                      placeholder="Mehmet Kaya"
                      className="contact-input"
                      data-testid="contact-name"
                    />
                  </Field>
                  <Field label="Ofis / Şirket" testid="field-company">
                    <input
                      required
                      type="text"
                      value={form.company}
                      onChange={onChange('company')}
                      placeholder="Antalya Realty"
                      className="contact-input"
                      data-testid="contact-company"
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="E-posta" testid="field-email">
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={onChange('email')}
                      placeholder="ad@ofis.com"
                      className="contact-input"
                      data-testid="contact-email"
                    />
                  </Field>
                  <Field label="Telefon" testid="field-phone">
                    <input
                      required
                      type="tel"
                      value={form.phone}
                      onChange={onChange('phone')}
                      placeholder="+90 ..."
                      className="contact-input"
                      data-testid="contact-phone"
                    />
                  </Field>
                </div>
                <Field label="Ofis Büyüklüğü" testid="field-size">
                  <div className="grid grid-cols-4 gap-2">
                    {['1-3', '4-10', '11-25', '25+'].map(opt => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setForm(s => ({ ...s, size: opt }))}
                        className="py-2.5 rounded-lg font-display text-[12px] tracking-[0.12em] transition-all"
                        style={{
                          background: form.size === opt ? 'linear-gradient(135deg,#E08A4F,#C56C39)' : 'rgba(244,236,223,0.06)',
                          border: form.size === opt ? '1px solid rgba(244,213,184,0.4)' : '1px solid rgba(244,236,223,0.12)',
                          color: form.size === opt ? '#1C130D' : '#F4ECDF',
                          fontWeight: form.size === opt ? 600 : 400,
                        }}
                        data-testid={`contact-size-${opt}`}
                      >
                        {opt} danışman
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Mesaj" testid="field-message">
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={onChange('message')}
                    placeholder="Demonuzda neyi görmek istersiniz? (opsiyonel)"
                    className="contact-input resize-none"
                    data-testid="contact-message"
                  />
                </Field>
                <button type="submit" className="btn-copper w-full inline-flex items-center justify-center gap-2.5 group" data-testid="contact-submit">
                  <span>Demoyu Talep Et</span>
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <p className="font-body text-[9px] tracking-[0.24em] uppercase text-[#F4ECDF]/40 text-center">
                  Verileriniz KVKK kapsamında korunmaktadır.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="font-body text-[10px] tracking-[0.32em] uppercase text-[#E08A4F] font-semibold mb-2">{label}</div>
      {children}
    </label>
  );
}

/* ============ FOOTER ============ */
function Footer() {
  const cols = [
    { title: 'Ürün', links: ['Neden DEO', 'Özellikler', 'Modüller', 'Fiyatlandırma'] },
    { title: 'Çözümler', links: ['Lüks Konut', 'Ticari Gayrimenkul', 'Yatırım Portföyü', 'Vacation Rentals'] },
    { title: 'Şirket', links: ['Hakkımızda', 'Kariyer', 'Blog', 'Basın Kiti'] },
    { title: 'Destek', links: ['SSS', 'Yardım Merkezi', 'KVKK', 'Kullanım Şartları'] },
  ];
  return (
    <footer className="px-6 lg:px-10 pt-20 pb-10 relative"
      style={{
        background: '#0B0703',
      }}
      data-testid="landing-footer"
    >
      <div className="max-w-[1280px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-4">
            <div className="mb-5">
              <DeoLogo size={56} variant="color" />
            </div>
            <p className="font-body text-[13px] leading-[1.7] text-[#F4ECDF]/55 max-w-[320px]">
              Türkiye'nin ilk sesli AI emlak panel sistemi. Ofisinizi 30 dakikada dijitalleştirin.
            </p>
            <div className="flex items-center gap-2 mt-6">
              <div className="capsule capsule-mint">
                <span className="w-1.5 h-1.5 rounded-full bg-[#68E5B2]" /> KVKK
              </div>
              <div className="capsule capsule-copper">ISO 27001</div>
            </div>
          </div>

          {cols.map(c => (
            <div key={c.title} className="lg:col-span-2">
              <Eyebrow light>{c.title}</Eyebrow>
              <ul className="mt-4 space-y-2.5">
                {c.links.map(l => (
                  <li key={l}>
                    <a href="#" className="font-body text-[13px] text-[#F4ECDF]/60 hover:text-[#E08A4F] transition-colors">
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-8 border-t border-white/8 flex flex-wrap items-center justify-between gap-4">
          <div className="font-body text-[10px] tracking-[0.28em] uppercase text-[#F4ECDF]/40">
            © {new Date().getFullYear()} DEO Dijital Emlak Ofisi · Tüm hakları saklıdır.
          </div>
          <div className="flex items-center gap-5">
            {['Instagram', 'LinkedIn', 'YouTube', 'X'].map(s => (
              <a key={s} href="#" className="font-body text-[12px] text-[#F4ECDF]/45 hover:text-[#E08A4F] transition-colors">
                {s}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ============ MAIN ============ */
export default function LandingPage() {
  return (
    <div className="min-h-screen relative" style={pageBg} data-testid="landing-page">
      <Nav />
      <Hero />
      <Stats />
      <WhyDeo />
      <Showcase />
      <AIFeatures />
      <Modules />
      <Regions />
      <Testimonials />
      <Pricing />
      <FAQ />
      <ContactForm />
      <Footer />
    </div>
  );
}
